import {
  sanitizeWorkRequestAttachmentFilename,
  type StoredWorkRequestAttachment,
} from "../../shared/work-request";

export type WorkRequestAttachmentKind = "image" | "pdf" | "video" | "other";

export interface ParsedWorkRequestAttachment {
  kind: WorkRequestAttachmentKind;
  mimeType: string | null;
  label: string;
  downloadName: string;
  href: string | null;
  isDataUrl: boolean;
  isR2: boolean;
  attachmentId: string | null;
}

type LegacyWorkRequestAttachment = string;
type WorkRequestAttachmentValue =
  | LegacyWorkRequestAttachment
  | StoredWorkRequestAttachment;

const DATA_URL_PATTERN = /^data:([^;,]+)?(;base64)?,([\s\S]*)$/i;

const MIME_EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "video/x-msvideo": "avi",
};

function extensionForMime(mimeType: string | null): string {
  if (!mimeType) return "bin";
  const normalized = mimeType.toLowerCase();
  if (MIME_EXTENSIONS[normalized]) return MIME_EXTENSIONS[normalized];
  if (normalized.startsWith("image/")) {
    const subtype = normalized.slice("image/".length).split("+")[0];
    return subtype || "img";
  }
  if (normalized.startsWith("video/")) return "mp4";
  return "bin";
}

function classifyMime(mimeType: string | null): WorkRequestAttachmentKind {
  if (!mimeType) return "other";
  const normalized = mimeType.toLowerCase();
  if (normalized.startsWith("image/")) return "image";
  if (normalized === "application/pdf") return "pdf";
  if (normalized.startsWith("video/")) return "video";
  return "other";
}

function fallbackLabel(
  kind: WorkRequestAttachmentKind,
  attachmentNumber: number,
): string {
  if (kind === "image") return `Image ${attachmentNumber}`;
  if (kind === "pdf") return `PDF ${attachmentNumber}`;
  if (kind === "video") return `Video ${attachmentNumber}`;
  return `Attachment ${attachmentNumber}`;
}

/**
 * Classify a stored work-request attachment value for safe display.
 * New attachments are R2 metadata; older rows may still be data URLs.
 */
export function parseWorkRequestAttachment(
  value: WorkRequestAttachmentValue,
  index: number,
): ParsedWorkRequestAttachment {
  const attachmentNumber = index + 1;

  if (typeof value !== "string") {
    const filename = sanitizeWorkRequestAttachmentFilename(value.filename);
    if (value.r2Key) {
      const mimeType = value.contentType?.toLowerCase() || null;
      const kind = classifyMime(mimeType);
      const extension = extensionForMime(mimeType);
      return {
        kind,
        mimeType,
        label: filename ?? fallbackLabel(kind, attachmentNumber),
        downloadName:
          filename ?? `attachment-${attachmentNumber}.${extension}`,
        href: null,
        isDataUrl: false,
        isR2: true,
        attachmentId: value.id ?? null,
      };
    }

    return parseStringAttachment(
      value.dataUrl ?? "",
      attachmentNumber,
      filename,
    );
  }

  return parseStringAttachment(value, attachmentNumber, null);
}

function parseStringAttachment(
  value: string,
  attachmentNumber: number,
  filename: string | null,
): ParsedWorkRequestAttachment {
  const trimmed = value.trim();

  if (!trimmed) {
    return {
      kind: "other",
      mimeType: null,
      label: filename ?? `Attachment ${attachmentNumber}`,
      downloadName: filename ?? `attachment-${attachmentNumber}.bin`,
      href: null,
      isDataUrl: false,
      isR2: false,
      attachmentId: null,
    };
  }

  const dataMatch = trimmed.match(DATA_URL_PATTERN);
  if (dataMatch) {
    const mimeType = (dataMatch[1] || "application/octet-stream").toLowerCase();
    const kind = classifyMime(mimeType);
    const extension = extensionForMime(mimeType);

    return {
      kind,
      mimeType,
      label: filename ?? fallbackLabel(kind, attachmentNumber),
      downloadName: filename ?? `attachment-${attachmentNumber}.${extension}`,
      href: trimmed,
      isDataUrl: true,
      isR2: false,
      attachmentId: null,
    };
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return {
      kind: "other",
      mimeType: null,
      label: filename ?? `Attachment ${attachmentNumber}`,
      downloadName: filename ?? `attachment-${attachmentNumber}`,
      href: trimmed,
      isDataUrl: false,
      isR2: false,
      attachmentId: null,
    };
  }

  return {
    kind: "other",
    mimeType: null,
    label: filename ?? `Attachment ${attachmentNumber}`,
    downloadName: filename ?? `attachment-${attachmentNumber}.bin`,
    href: null,
    isDataUrl: false,
    isR2: false,
    attachmentId: null,
  };
}
